import React, { useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table'
import { View, Text, Pressable, StyleSheet, ActivityIndicator, ScrollView } from 'react-native'
import { useAppTheme } from '#app/theme'

interface TableProps<T> {
  data: T[]
  columns: ColumnDef<T>[]
  onRowClick?: (row: T) => void
  isLoading?: boolean
  totalCount: number
  currentPage: number
  onPageChange: (page: number) => void
  actionsWidth?: number
}

export function Table<T>({
  data,
  columns,
  onRowClick,
  isLoading,
  totalCount,
  currentPage,
  onPageChange,
  actionsWidth = 40,
}: TableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const theme = useAppTheme()
  const pageSize = 25 // Match the server's page size

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const totalPages = Math.ceil(totalCount / pageSize)

  if (isLoading) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    )
  }

  if (data.length === 0) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <Text style={[styles.emptyText, { color: theme.textColor }]}>No data available</Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <View style={[styles.header, { borderBottomColor: theme.border.borderColor }]}>
        {table.getHeaderGroups().map(headerGroup => (
          <View key={headerGroup.id} style={styles.headerRow}>
            {headerGroup.headers.map((header, index) => {
              const isLastColumn = index === headerGroup.headers.length - 1
              return (
                <Pressable
                  key={header.id}
                  onPress={header.column.getToggleSortingHandler()}
                  style={[
                    styles.headerCell,
                    isLastColumn && { width: actionsWidth }
                  ]}
                >
                  <Text style={[styles.headerText, { color: theme.textColor }]}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {{
                      asc: ' 🔼',
                      desc: ' 🔽',
                    }[header.column.getIsSorted() as string] ?? null}
                  </Text>
                </Pressable>
              )
            })}
          </View>
        ))}
      </View>
      <ScrollView style={styles.body}>
        {table.getRowModel().rows.map(row => (
          <Pressable
            key={row.id}
            style={[styles.row, { borderBottomColor: theme.border.borderColor }]}
          >
            {row.getVisibleCells().map((cell, index) => {
              const isLastColumn = index === row.getVisibleCells().length - 1
              return (
                <View 
                  key={cell.id} 
                  style={[
                    styles.cell,
                    isLastColumn && { width: actionsWidth }
                  ]}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </View>
              )
            })}
          </Pressable>
        ))}
      </ScrollView>
      <View style={[styles.pagination, { borderTopColor: theme.border.borderColor }]}>
        <View style={styles.paginationInfo}>
          <Text style={[styles.paginationText, { color: theme.textColor }]}>
            Page {currentPage + 1} of {totalPages}
          </Text>
          <Text style={[styles.paginationText, { color: theme.textColor, marginLeft: 10 }]}>
            ({totalCount} total)
          </Text>
        </View>
        <View style={styles.paginationControls}>
          <Pressable
            onPress={() => onPageChange(0)}
            disabled={currentPage === 0}
            style={[styles.paginationButton, currentPage === 0 && styles.paginationButtonDisabled]}
          >
            <Text style={[styles.paginationButtonText, { color: theme.textColor }]}>{'<<'}</Text>
          </Pressable>
          <Pressable
            onPress={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
            style={[styles.paginationButton, currentPage === 0 && styles.paginationButtonDisabled]}
          >
            <Text style={[styles.paginationButtonText, { color: theme.textColor }]}>{'<'}</Text>
          </Pressable>
          <Pressable
            onPress={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
            style={[styles.paginationButton, currentPage >= totalPages - 1 && styles.paginationButtonDisabled]}
          >
            <Text style={[styles.paginationButtonText, { color: theme.textColor }]}>{'>'}</Text>
          </Pressable>
          <Pressable
            onPress={() => onPageChange(totalPages - 1)}
            disabled={currentPage >= totalPages - 1}
            style={[styles.paginationButton, currentPage >= totalPages - 1 && styles.paginationButtonDisabled]}
          >
            <Text style={[styles.paginationButtonText, { color: theme.textColor }]}>{'>>'}</Text>
          </Pressable>
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  centerContent: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  header: {
    borderBottomWidth: 1,
  },
  headerRow: {
    flexDirection: 'row',
  },
  headerCell: {
    padding: 12,
    justifyContent: 'center',
    flex: 1,
  },
  headerText: {
    fontWeight: 'bold',
  },
  body: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  cell: {
    padding: 12,
    justifyContent: 'center',
    flex: 1,
  },
  cellText: {
    fontSize: 14,
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
  },
  pagination: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderTopWidth: 1,
  },
  paginationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  paginationText: {
    fontSize: 14,
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  paginationButton: {
    padding: 8,
    marginHorizontal: 4,
    borderRadius: 4,
    opacity: 1,
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
}) 